import React, { useEffect } from "react";
import classnames from "classnames";
import { useFeatureFlag } from "../../context/FeatureFlagContext";
import { LiveChatOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/LiveChatOutlined";
import { shouldShowMIAWChat } from "./utils";
import { MONETATE_EVENT_NAMES, trackMonetateEvent } from "../../context/MonetateContext";

import styles from "./live-chat-button.module.scss";
import { TickCircleFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleFilled";

const LiveChatButton = React.memo(
  ({
    startButtonText = "Start chat",
    buttonLocation,
    breadcrumb = "",
    channel,
    className = "",
    showChatIcon = false,
    liveChatInEmailUs = false,
    liveChatForDamagedItems = false,
    isFetchingNewBreadcrumb = false,
  }) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const submittingTimeOut = 5000;

    const showMIAWChat = useFeatureFlag("show_miaw_chat") || shouldShowMIAWChat(channel, buttonLocation);

    React.useEffect(() => {
      const onStartSuccessful = event => {
        event.preventDefault();
        setIsSubmitting(false);
      };
      const onMIAWInitFailure = event => {
        event.preventDefault();

        showMIAWChat ? initialiseMIAWChat() : initialiseLiveChat();
      };
      window.addEventListener("jl-digital-conversations-chat-started-successfully", onStartSuccessful);
      window.addEventListener("jl-MIAW-conversations-chat-initialisation-failed", onMIAWInitFailure);
      return () => {
        window.removeEventListener("jl-digital-conversations-chat-started-successfully", onStartSuccessful);
        window.removeEventListener("jl-MIAW-conversations-chat-initialisation-failed", onMIAWInitFailure);
      };
    }, []);

    React.useEffect(() => {
      // This was to satisfy the linter of consistent return, this cleanup by definition does nothing
      if (!isSubmitting) return () => {};

      const interval = setTimeout(() => {
        setIsSubmitting(false);
      }, submittingTimeOut);

      return () => {
        clearInterval(interval);
      };
    }, [isSubmitting]);

    useEffect(() => {
      setIsSubmitting(isFetchingNewBreadcrumb);
    }, [isFetchingNewBreadcrumb]);

    const getBreadcrumbFromButtonLocation = buttonLocation => {
      const subLocations = buttonLocation.split(" | ");
      const breadCrumbCategory = subLocations[1];
      const breadCrumbSubCategory = subLocations.pop();

      switch (breadCrumbCategory) {
        case "deliveryQueries":
          return breadCrumbCategory;
        case "returnsAndRefunds":
          return breadCrumbSubCategory === "returningOrExchanging"
            ? `${breadCrumbCategory}-returning`
            : `${breadCrumbCategory}-refunds`;
        case "problemsWithProduct":
          return breadCrumbSubCategory === "other" ? `${breadCrumbCategory}-other` : `${breadCrumbCategory}-electrical`;
        case "productStockInformation":
        case "placeEditCancelOrder":
        case "myJLMembership":
          return breadCrumbCategory;
        case "myAccountOnline":
          return "myJLMembership";
        case "repair":
          return "problemsWithProduct-electrical";

        default:
          return breadCrumbCategory;
      }
    };

    const initialiseMIAWChat = () => {
      window.dispatchEvent(
        new CustomEvent("jl-MIAW-conversations-start-chat", {
          detail: {
            channel,
            buttonLocation: breadcrumb || getBreadcrumbFromButtonLocation(buttonLocation),
          },
        }),
      );
    };

    const initialiseLiveChat = () => {
      window.dispatchEvent(
        new CustomEvent("jl-digital-conversations-start-chat", {
          detail: {
            channel,
            buttonLocation,
          },
        }),
      );
    };

    // This function is called when the chat start button is clicked.
    // It dispatches a custom event to start the chat and tracks the engagement with Monetate.
    // The dependency array includes all the variables that are used within the function
    // to ensure that it has access to the latest values when it's called.
    const dispatchStartEvent = React.useCallback(
      event => {
        event.preventDefault();
        setIsSubmitting(true);
        // Track the live chat engagement event in Monetate when the button is clicked as part of the AB6553 experiment
        liveChatInEmailUs && trackMonetateEvent(MONETATE_EVENT_NAMES.AB6553LiveChatEngagementRate);
        showMIAWChat ? initialiseMIAWChat() : initialiseLiveChat();
      },
      [breadcrumb, channel, buttonLocation, showMIAWChat],
    );

    const renderChatButton = () => (
      <button
        type="button"
        data-test="live-chat-button"
        className={classnames(styles["live-chat-button"], className, { [styles.disabled]: isSubmitting })}
        onClick={dispatchStartEvent}
      >
        <div className={classnames({ [styles["live-chat-button-loading"]]: isSubmitting })} />
        <span className={classnames({ [styles.hidden]: isSubmitting, [styles.showChatIcon]: showChatIcon })}>
          {showChatIcon && <LiveChatOutlined />}
          {startButtonText}
        </span>
      </button>
    );

    const renderChatButtonInEmailUs = () => {
      return (
        <div
          className={classnames(styles["live-chat-button-email-us"], className)}
          data-test="live-chat-button-email-us"
        >
          <h2 className={classnames(styles["heading"])}>Solve your issue faster with Live Chat</h2>
          {renderChatButton()}
          <p className={classnames(styles["help-text"])}>
            <span>
              <TickCircleFilled />
              You’ll be connected with a Partner agent
            </span>
            <span>
              <TickCircleFilled />
              You can send photos if needed
            </span>
            <span>
              <TickCircleFilled />
              You can save a copy of the chat for your records
            </span>
          </p>
        </div>
      );
    };

    const renderChatButtonForDamagedItems = () => {
      return (
        <div
          className={classnames(styles["live-chat-button-damaged-items"], className)}
          data-test="live-chat-button-damaged-items"
        >
          <h2 className={classnames(styles["heading"])}>Live chat</h2>
          <p className={classnames(styles["help-text"])}>
            <span>
              <TickCircleFilled />
              Get answers faster with one of our agents
            </span>
            <span>
              <TickCircleFilled />
              Save a chat transcript for your records
            </span>
            <span>
              <TickCircleFilled />
              Send us photos if needed
            </span>
          </p>
          {renderChatButton()}
          <span className={classnames(styles.chatTimings)}>Available 7 days a week, 8am to 8pm.</span>
        </div>
      );
    };

    return liveChatInEmailUs
      ? renderChatButtonInEmailUs()
      : liveChatForDamagedItems
        ? renderChatButtonForDamagedItems()
        : renderChatButton();
  },
);

export default LiveChatButton;
